import React, { useContext, useEffect } from "react";
import { decodeToken, storeApiToken } from "../api/core";
import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";

export interface IDefaultUser {
    roles: string[];
    email: string;
    _id: string;
}

export type DefaultUser = IDefaultUser | null;

export interface IUserContext<IUser> {
    user: IUser | null;
    setUser: (u: IUser) => void;
}

export const UserContext = React.createContext<IUserContext<DefaultUser>>({
    user: null,
    setUser: (u: DefaultUser | null) => { },
});

export const useUser = () => useContext(UserContext);

export const login = <IUser = DefaultUser>(context: IUserContext<IUser>, token: string) => {
    storeApiToken(token);
    const decoded = decodeToken<IUser>(token);
    context.setUser(decoded);
    return decoded;
}

interface IUserWithRoles {
    roles: string[];
}

export type DefaultUserWithRoles = IUserWithRoles | null;

export const checkRole = <RolesUser extends DefaultUserWithRoles>(user: RolesUser, role: string) => user && user.roles.find(r => r === role) ? true : false;

export const checkContextRole = <RolesUser extends DefaultUserWithRoles>(context: IUserContext<RolesUser>, role: string) => checkRole(context.user, role);




export const useLogoutSoonWarning = (warnWhenLeftMinutes: number = 5) => {
  const { user } = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();

  useEffect(() => {
    const exp = (user as any)?.exp;
    if(exp) {
      const checkAndWarn = () => {
        const now = new Date();
        const expires = new Date(exp*1000);
        const diff = ((expires as any) - (now as any)) / (1000*60);
        if(diff < warnWhenLeftMinutes) {
          enqueueSnackbar({
            autoHideDuration: 10000,
            preventDuplicate: true,
            message: formatMessage(
              { id: diff > 0 ? "login.auth_expires_soon" : "login.auth_expired" },
              { minutes: Math.floor(Math.abs(diff))}),
          })
        }
      }
      const interval = setInterval(checkAndWarn, 2*60*1000);
      checkAndWarn();
      return () => clearInterval(interval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, warnWhenLeftMinutes]);
}


export const LogoutSoonWarning = (props: { warnWhenLeftMinutes?: number }) => {
  useLogoutSoonWarning(props.warnWhenLeftMinutes);
  return null;
}